import { useMemo, useState, useEffect, FC } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generatePath } from 'react-router';
import { routesPath } from '@router/routes';
import { Tabs } from '@dynamika-shared/ui/Tabs/Tabs';
import { useValidationSpecialistSubjectsSlice } from '@restore/ValidationSpecialistSubjects/ValidationSpecialistSubjects.slice';
import { useDefaultVisitSelection } from '@hooks/useDefaultVisitSelection';
import { getVisitKey } from '@utils/getVisitKey';
import { VisitTabTooltip } from '@components/VisitTabTooltip';
import { SubjectDetailsPatient } from './SubjectDetailsPatient';
import { VisitTabLabel } from './components/VisitTabLabel';
import { renderSkeleton } from './components/renderSkeleton';
import { useBreadcrumbs } from 'src/hooks/useBreadcrumbs';
import { VSUploads } from '../VSUploads/VSUploads';
import './SubjectDetails.scss';

export const SubjectDetails: FC = () => {
  const { patientId, clinicalTrialId } = useParams<{
    patientId: string;
    clinicalTrialId?: string;
  }>();

  const navigate = useNavigate();

  const [state, actions] = useValidationSpecialistSubjectsSlice((s, a) => [
    s,
    a,
    s?.context,
  ]);

  const [selectedVisit, setSelectedVisit] = useState('');

  const { subjectDetails, loading } = state;
  const visits = useMemo(
    () => subjectDetails?.visits || [],
    [subjectDetails?.visits]
  );

  // Keys of visits that have uploads to validate (the only interactive ones).
  const interactiveKeys = useMemo(
    () =>
      new Set(
        visits.filter(v => v.numberUploadsToValidate).map(v => getVisitKey(v))
      ),
    [visits]
  );

  const activeVisitKey = selectedVisit || getVisitKey(visits[0]);
  const currentVisit = visits.find(v => getVisitKey(v) === activeVisitKey);
  const visitName = currentVisit?.visitName || '...';

  useBreadcrumbs(
    [
      ['Validate List', 'validationSpecialistSubjectsClinicalTrial'],
      [
        subjectDetails?.patientCode ?? '...',
        'validationSpecialistSubjectDetailClinicalTrial',
      ],
      [visitName, 'validationSpecialistSubjectDetailClinicalTrial'],
    ],
    [clinicalTrialId, patientId]
  );

  useEffect(() => {
    setSelectedVisit('');
    actions?.clearSubjectDetails?.();

    if (
      patientId &&
      clinicalTrialId &&
      actions?.fetchValidationSpecialistSubjectDetails
    ) {
      actions.fetchValidationSpecialistSubjectDetails({
        patientId,
        clinicalTrialId,
      });
    }

    return () => {
      actions?.clearSubjectDetails?.();
    };
  }, [patientId, clinicalTrialId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (clinicalTrialId && actions?.fetchContext) {
      actions.fetchContext({ clinicalTrialId });
    }
  }, [clinicalTrialId, actions]);

  // Visits without uploads to validate are disabled, so default to one with uploads.
  useDefaultVisitSelection(
    visits,
    selectedVisit,
    setSelectedVisit,
    interactiveKeys
  );

  const tabsItems = visits.map(visit => {
    const visitKey = getVisitKey(visit);
    const hasUploads = interactiveKeys.has(visitKey);
    return {
      key: visitKey,
      // Visits without uploads to validate are disabled.
      disabled: !hasUploads,
      label: (
        <VisitTabTooltip
          visitName={visit.visitName}
          hasData={hasUploads}
          isActive={visitKey === selectedVisit}
        >
          <VisitTabLabel
            title={visit.visitName}
            count={visit.numberUploadsToValidate}
          />
        </VisitTabTooltip>
      ),
      children: null,
    };
  });

  const handleTabChange = (visitId: string) => {
    setSelectedVisit(visitId);
  };

  const goBack = () => {
    if (!clinicalTrialId) return;
    const url = generatePath(
      routesPath.validationSpecialistSubjectsClinicalTrial,
      { clinicalTrialId }
    );
    navigate(url);
  };

  return (
    <div className='min-h-[calc(100%-5.6rem)] pb-10'>
      <SubjectDetailsPatient
        patientCode={subjectDetails?.patientCode || patientId!}
        status={subjectDetails?.status}
        dob={subjectDetails?.dob}
        siteName={subjectDetails?.siteName}
        cohortId={subjectDetails?.cohortId}
        onBack={goBack}
      />

      {tabsItems.length > 0 && (
        <div className='mb-4'>
          <Tabs
            defaultActiveKey={selectedVisit}
            activeKey={selectedVisit}
            items={tabsItems}
            onChange={handleTabChange}
            blocked={false}
          />
        </div>
      )}

      <div className='flex-1 overflow-auto'>
        {loading && renderSkeleton()}

        {!loading && selectedVisit && currentVisit && (
          <VSUploads
            visitId={currentVisit.id}
            visitDefinitionId={currentVisit.visitDefinitionId}
            visitName={currentVisit.visitName}
            visitStatus={currentVisit.status}
          />
        )}
      </div>
    </div>
  );
};
